import { lazy, Suspense, useState, useEffect, useId, useRef } from "react";
import { SecondFactorSkeleton } from "@/components/SecondFactorSkeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isEmail } from "@/lib/authValidation";
import { apiRequest, fetchJson, resetCsrfToken } from "@/lib/queryClient";
import { describeApiError, localizeApiMessage, parseApiError } from "@/lib/apiError";
import { msg, type Msg } from "@shared/i18n";
import { cn } from "@/lib/utils";
import { postLogout } from "@/lib/logout";
import { GoogleLogo } from "@/components/icons/GoogleLogo";
import { MicrosoftLogo } from "@/components/icons/MicrosoftLogo";
import { ArrowLeft, Eye, EyeOff, KeyRound, Mail } from "lucide-react";
import { AuthCard, AuthPageShell } from "@/components/AuthPageShell";
import { SessionEndedCard } from "@/components/SessionEndedCard";
import { AuthMessage, type TwoFactorStatusResponse } from "@/components/AuthMessage";
/* The second factor loads when the password step is passed, not with the
   sign-in page itself: most visits to this page are the password alone. */
const SecondFactorPanel = lazy(() => import("@/components/SecondFactorPanel").then((m) => ({ default: m.SecondFactorPanel })));
import {
  isPasskeySupported,
  supportsAutofill,
  loginWithPasskey,
  startPasskeyAutofill,
  resetPasskeyAutofill,
  PasskeyVerificationError,
} from "@/lib/passkeys";
import { canResumeWithPasskey } from "@/lib/sessionResume";
import { markPasskeyOnThisDevice } from "@/lib/passkeyDevice";
import { suppressStepUp } from "@/lib/stepUp";
import { releaseStuckOverlayPointerEvents } from "@/lib/overlayCleanup";
import { appSignInAboutPath, appSignInAbortPath, clearAppSignIn, peekAppSignIn, rememberAppSignIn } from "@/lib/appSignIn";

/**
 * A SIGN-IN FORM MUST NOT ENFORCE A PASSWORD POLICY.
 *
 * This required six characters and reported "Password must be at least 6
 * characters" on the LOGIN screen. That is a sign-UP rule in the wrong place:
 * it publishes the policy minimum to anyone who submits the form empty, and it
 * locks out any account whose password predates the rule — the user cannot even
 * reach the server to be told to reset it.
 *
 * The server's own loginSchema is `password: z.string().min(1).max(MAX_PASSWORD)`
 * and has always been right. Match it: check that something was typed, and let
 * authentication decide the rest. The max is what protects bcrypt from an
 * oversized input, and that check lives on the server where it belongs.
 */
type LoginForm = {
  email: string;
  password: string;
};

type LoginErrors = Partial<Record<keyof LoginForm, string>>;

const validateLogin = (v: LoginForm, emailError: string, passwordError: string): LoginErrors => ({
  ...(isEmail(v.email) ? {} : { email: emailError }),
  ...(v.password.length >= 1 ? {} : { password: passwordError }),
});

/*
  Two fields and two rules, held in plain state rather than react-hook-form.
  This page is the one page in the entry chunk (App.tsx says why), so
  whatever it imports, every signed-in person downloads on every first load:
  react-hook-form and its <Form> wrappers were ~15 KB gzip of that, to check
  "is this an email" and "was something typed". Behaviour is what the form
  had: nothing is checked until the first submit, a field is re-checked as it
  changes after that, and a failed submit focuses the first field in error.
*/

/**
 * What is on the card. One task at a time, and no route change between them —
 * the address typed at the top is still there on the way back (G10).
 *
 *   signin — the ways in
 *   factor — the password was right; the second factor, inline
 *   reset  — "forgot your password?", asking for the link
 */
type Stage = "signin" | "factor" | "reset";

/** `?error=` codes the OAuth callbacks hand back. */
const OAUTH_ERRORS: Record<string, Msg> = {
  auth_failed: msg("shellAuthOauthFailed", "Signing in with that provider didn't complete. Try again, or use another way below."),
  session_error: msg("shellAuthOauthSessionError", "Couldn't finish signing you in. Please try again."),
  login_failed: msg("shellAuthOauthSessionError", "Couldn't finish signing you in. Please try again."),
  no_account: msg("shellAuthOauthNoAccount", "There's no SaleFish account for that email. Ask your administrator to invite you."),
  // "Sign in with SaleFish" from another app (server/oidc/routes.ts).
  app_signin_expired: msg("shellAuthAppSignInExpired", "That sign-in has expired. Start again from the app you were signing in to."),
  app_signin_failed: msg("shellAuthAppSignInFailed", "Couldn't finish signing you in to the app. Start again from the app."),
};

export default function Login() {
  const { toast } = useToast();
  const { checkSession } = useAuth();
  const t = useTranslation();
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkPassword, setLinkPassword] = useState("");
  const [isLinking, setIsLinking] = useState(false);
  const [pendingLinkInfo, setPendingLinkInfo] = useState<{ email: string; provider: string } | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showLinkPassword, setShowLinkPassword] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [stage, setStage] = useState<Stage>("signin");
  const [error, setError] = useState<string | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);

  /* Signing in for another app ("Sign in with SaleFish", lib/appSignIn.ts).
   * Read from the address once, then from this tab's storage, so it survives
   * a Google or Microsoft round trip and a reload. Arriving at plain /login
   * (no app, no error being reported) means that sign-in was abandoned. */
  const [appSignIn] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("app");
    if (fromUrl) {
      rememberAppSignIn(fromUrl);
      return peekAppSignIn();
    }
    if (!params.has("error") && !params.has("link_required")) {
      clearAppSignIn();
      return null;
    }
    return peekAppSignIn();
  });
  const [appName, setAppName] = useState<string | null>(null);

  /* The installed app arriving on a lapsed session, with a passkey on this
   * device: one control instead of the whole menu (G9). Read once, on mount —
   * display mode cannot change without a relaunch. Standing it down is one-way:
   * somebody who asked for another way does not want the short screen back. */
  const [resume, setResume] = useState(() => !appSignIn && canResumeWithPasskey(isPasskeySupported()));
  const [unlocking, setUnlocking] = useState(false);

  const savedEmail = localStorage.getItem('rememberedEmail');
  const [email, setEmail] = useState(savedEmail || "");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<LoginErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const fieldId = useId();
  const emailId = `${fieldId}-email`;
  const passwordId = `${fieldId}-password`;
  const validate = (v: LoginForm) =>
    validateLogin(v, t("emailValidationError", "Please Enter a Valid Email Address"), t("passwordValidationError", "Enter Your Password"));
  /** After the first submit, a field is re-checked as it changes (and only that field). */
  const recheck = (field: keyof LoginForm, next: LoginForm) => {
    if (!submitted) return;
    const message = validate(next)[field];
    setFieldErrors((prev) => ({ ...prev, [field]: message }));
  };
  /* Email & password is revealed on demand, as PropPay's sign-in has it: the
     passkey and the two identity providers are the ways in we recommend, and
     a password form open by default reads as the main one. Someone who signs
     in with email and ticked "Remember me" gets it open — that is how they
     come in. */
  const [showEmail, setShowEmail] = useState(() => !!savedEmail);
  const revealEmail = () => {
    setError(null);
    setShowEmail(true);
    // Straight into the field where a keyboard is already there.
    if (!window.matchMedia("(pointer: coarse)").matches) setTimeout(() => emailRef.current?.focus(), 0);
  };

  // Release any `pointer-events: none` a Radix overlay (e.g. the user-menu dropdown
  // that hosts "Sign Out") left stuck on <body> when it unmounted mid-close during
  // navigation here. Without this the whole login page is unclickable — passkey and
  // social buttons silently do nothing — until a manual refresh. rAF re-clears it after
  // paint in case the unmounting overlay restores the style a tick later (Safari).
  useEffect(() => {
    releaseStuckOverlayPointerEvents();
    const raf = requestAnimationFrame(() => releaseStuckOverlayPointerEvents());
    return () => cancelAnimationFrame(raf);
  }, []);

  // Account linking, and failures handed back by the OAuth callbacks.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthError = urlParams.get('error');
    if (oauthError) {
      const known = OAUTH_ERRORS[oauthError];
      setError(known ? t(known) : t("shellAuthOauthUnknownError", "Sign-in didn't complete. Please try again."));
    }
    if (urlParams.get('link_required') === 'true') {
      apiRequest('GET', '/api/auth/pending-link', undefined, { authOptional: true })
        .then((r) => r.json())
        .then((data) => {
          setPendingLinkInfo(data);
          setShowLinkDialog(true);
        })
        .catch((err) => console.error('Error checking pending link:', err));
    }
    if (oauthError || urlParams.has('link_required')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Which app this is for, in its own words ("Showroom"). An expired sign-in
  // says so here rather than at the end, after the person has typed a password.
  useEffect(() => {
    if (!appSignIn) return;
    const params = new URLSearchParams(window.location.search);
    if (params.has("app")) {
      params.delete("app");
      const rest = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
    }
    let cancelled = false;
    fetchJson<{ app: string }>(appSignInAboutPath(appSignIn), { authOptional: true })
      .then((r) => {
        if (!cancelled) setAppName(r.app);
      })
      .catch(() => {
        if (cancelled) return;
        clearAppSignIn();
        setError(t("shellAuthAppSignInExpired", "That sign-in has expired. Start again from the app you were signing in to."));
      });
    return () => {
      cancelled = true;
    };
  }, [appSignIn]);

  useEffect(() => {
    if (savedEmail) setRememberMe(true);
  }, [savedEmail]);

  // While the card is asking for the second factor, requests fired underneath
  // it must not stack the step-up dialog on top of it.
  useEffect(() => {
    if (stage !== "factor") return;
    return suppressStepUp();
  }, [stage]);

  // Check passkey support and start conditional-UI autofill.
  // Runs ONCE on mount. checkSession is read through a ref so it is not an effect dep —
  // otherwise the effect re-fires on every render (checkSession is a fresh function each
  // render), re-hitting the authenticate/options endpoint and restarting the autofill
  // ceremony repeatedly.
  const checkSessionRef = useRef(checkSession);
  checkSessionRef.current = checkSession;
  const resumeRef = useRef(resume);
  resumeRef.current = resume;
  /**
   * Put the conditional-UI ceremony back after the passkey BUTTON has finished
   * with the slot.
   *
   * The button tears the autofill ceremony down to open its own prompt
   * (Safari runs one ceremony at a time). Nothing used to put it back, so a
   * single cancelled or failed press killed browser autofill for the rest of
   * the page's life — the passkey stopped appearing in the email field's
   * dropdown, and only a full page load brought it back. That is the same
   * "works again after a hard refresh" shape as the button itself.
   */
  const restartAutofillRef = useRef<() => void>(() => {});
  useEffect(() => {
    let cancelled = false;
    const runAutofill = async () => {
      try {
        await startPasskeyAutofill();
        markPasskeyOnThisDevice();
        if (!cancelled) {
          await checkSessionRef.current();
        }
      } catch (err) {
        // Most ways this ends are not failures. An ignored prompt, a ceremony
        // the "Sign in with a passkey" button took over, one cleared by a
        // sign-out — none of those is something the user did and watched go
        // wrong, and a toast for any of them would fire on ordinary page loads.
        //
        // A verify that failed AFTER the authenticator accepted them is the
        // opposite: they held their finger down, it worked, and the page did
        // nothing. So this one case is loud, and lands in the same place the
        // button's failures do.
        if (err instanceof PasskeyVerificationError) {
          console.error('[passkey] autofill verify failed after authentication:', err.message, err);
          // Gated on `cancelled`, and that loses nothing: an unmount here is
          // usually a REMOUNT, and the remounted instance joins the SAME shared
          // promise, so the message still comes from whichever login page is
          // actually on screen.
          if (!cancelled) {
            setShowEmail(true);
            setError(
              t(
                "shellAuthPasskeyVerifyFailed",
                "Your passkey was accepted by this device but the sign-in could not be finished. Try the passkey button, or use your email and password.",
              ),
            );
          }
        } else if (import.meta.env.DEV) {
          console.debug('[passkey] autofill ended without a sign-in:', err);
        }
      }
    };

    (async () => {
      if (!isPasskeySupported()) return;
      const autofillOk = await supportsAutofill();
      if (!cancelled) setPasskeySupported(true);
      if (!autofillOk) return;
      // Only a browser that HAS conditional UI gets a restart hook; on the rest
      // (Firefox) startAuthentication would refuse the autofill ceremony and the
      // retry would be a wasted challenge.
      restartAutofillRef.current = () => {
        if (cancelled) return;
        // Forget the ceremony the button just aborted before asking for a new
        // one — the dedupe would otherwise hand that dead entry straight back.
        resetPasskeyAutofill();
        void runAutofill();
      };
      // The Unlock screen has no email field for the browser to attach the
      // autofill ceremony to; it starts when that screen stands down (below).
      if (resumeRef.current) return;
      await runAutofill();
    })();
    return () => {
      cancelled = true;
      // Deliberately does NOT cancel the ceremony here.
      //
      // This page unmounts and remounts on its own: AuthContext swaps the whole
      // tree for a loader every time checkSession flips isLoading. Cancelling on
      // unmount aborted the in-flight autofill, which let the remount start a
      // fresh one — and each start burns a new server challenge, stranding the
      // ceremony before it. That loop is what produced eight
      // authenticate/options calls in 19 seconds with zero verifies in
      // production on 2026-08-11.
    };
  }, []);

  // The Unlock screen stood down: the email field now exists, so conditional
  // UI has somewhere to offer the passkey.
  const wasResume = useRef(resume);
  useEffect(() => {
    if (wasResume.current && !resume) restartAutofillRef.current();
    wasResume.current = resume;
  }, [resume]);

  const handleOAuthLogin = (provider: 'google' | 'microsoft') => {
    localStorage.removeItem('selectedProject');
    localStorage.removeItem('selectedPhase');
    localStorage.removeItem('isProjectSelected');
    window.gtag?.('event', 'login', { method: provider });
    window.location.href = `/api/auth/${provider}`;
  };

  const handlePasskeyLogin = async () => {
    setError(null);
    try {
      await loginWithPasskey(email || undefined);
      // A synced passkey used here is one this device holds, enrolled here or
      // not — it drives the Unlock screen and the enrolment offer.
      markPasskeyOnThisDevice();
      window.gtag?.('event', 'login', { method: 'passkey' });
      await checkSession();
    } catch (err: any) {
      // Browsers return NotAllowedError/AbortError both when the user dismisses the prompt
      // AND when no passkey for this site exists on the device — they don't distinguish the
      // two (privacy). Either way, fall back gracefully to the standard login already on this
      // page rather than showing a hard error: guide them to email/password and focus it.
      const name = err?.name;
      console.error('[passkey] sign-in failed:', name || 'unknown', '—', err?.message, err);
      const isNoPasskeyOrCancel = name === "NotAllowedError" || name === "AbortError";
      setShowEmail(true); // the message points at the email form, so open it
      setError(
        isNoPasskeyOrCancel
          ? t(
              "shellAuthPasskeyNotUsed",
              "No passkey was used. Sign in with your email and password below — once you're in, you can add a passkey from Account → Security.",
            )
          : t("shellAuthPasskeyStartFailed", "Couldn't start passkey sign-in ({name}: {detail}). Use email and password below.", {
              name: name || 'error',
              detail: err?.message || 'unknown',
            }),
      );
      (emailRef.current ?? document.querySelector<HTMLInputElement>('[data-testid="input-email"]'))?.focus();
      // The button consumed the browser's one ceremony slot to get here. Give it
      // back to conditional UI, so the passkey is still offered in the email
      // field's dropdown and a second press has a live ceremony to cancel.
      restartAutofillRef.current();
    }
  };

  /** The Unlock screen's one button: tap, face, in (G9). */
  const handleUnlock = async () => {
    setError(null);
    setUnlocking(true);
    try {
      await loginWithPasskey();
      markPasskeyOnThisDevice();
      window.gtag?.('event', 'login', { method: 'passkey' });
      await checkSession();
    } catch (err: any) {
      // The signal behind this screen is local and can be wrong — a passkey that
      // was on this device last month may be gone. So a failed ceremony does
      // not retry: it drops to the full menu with the reason showing.
      console.warn('[passkey] unlock failed:', err?.name, err?.message);
      const name = err?.name;
      setError(
        name === "NotAllowedError" || name === "AbortError"
          ? t("shellAuthUnlockNotUsed", "The passkey wasn't used. Choose another way to sign in.")
          : t("shellAuthUnlockFailed", "Couldn't unlock with your passkey. Choose another way to sign in."),
      );
      setResume(false);
    } finally {
      setUnlocking(false);
    }
  };

  /** Signed in and fully verified — let the session check take it from here. */
  const finishSignIn = async () => {
    setFinishing(true);
    toast({
      title: t("loginSuccessful", "Signed In"),
      description: t("welcomeBack", "Welcome back!"),
    });
    // AuthContext spends the remembered destination (lib/sessionEnd.ts) — the
    // page a notification or bookmark was opening when the session ran out.
    await checkSession();
    setFinishing(false);
  };

  const handleEmailLogin = async (values: LoginForm) => {
    setError(null);
    try {
      await apiRequest("POST", "/api/auth/login", values);

      if (rememberMe) {
        localStorage.setItem('rememberedEmail', values.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      localStorage.removeItem('selectedProject');
      localStorage.removeItem('selectedPhase');
      localStorage.removeItem('isProjectSelected');

      // Clear dismissed service notifications so they show again after login
      sessionStorage.removeItem('dismissed-notification-id');

      window.gtag?.('event', 'login', { method: 'email' });

      // The password was right. Does this session still owe a second factor?
      // Ask BEFORE telling AuthContext, so the factor is asked for right here
      // in the card — no route change, the address still typed above — rather
      // than on a separate /verify page.
      let status: TwoFactorStatusResponse | null = null;
      try {
        status = await fetchJson<TwoFactorStatusResponse>("/api/auth/2fa/status", { authOptional: true });
      } catch {
        status = null; // unknown — the session check decides, and /verify is the fallback
      }
      if (status?.required) {
        setPassword("");
        setStage("factor");
        return;
      }
      await finishSignIn();
    } catch (error: any) {
      let errorMessage = describeApiError(error, t("invalidCredentials", "Invalid Credentials"));
      const lowerMessage = errorMessage.toLowerCase();
      if (lowerMessage.includes("oauth") || lowerMessage.includes("social") || lowerMessage.includes("provider")) {
        errorMessage = `${t("oauthAccountDetected", "OAuth Account Detected")}. ${t("oauthAccountDescription", "Your account was created using Google or Microsoft sign-in. Please use the social login button above.")}`;
      }
      window.gtag?.('event', 'login_failed', { method: 'email', reason: errorMessage.slice(0, 100) });
      setError(localizeApiMessage(errorMessage, t));
    }
  };

  const submitEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    // Fetch the second-factor panel while the password is checked, so the
    // next step appears without a wait.
    void import("@/components/SecondFactorPanel");
    const values = { email, password };
    const errors = validate(values);
    setSubmitted(true);
    setFieldErrors(errors);
    if (errors.email) return emailRef.current?.focus();
    if (errors.password) return passwordRef.current?.focus();
    setSubmitting(true);
    try {
      await handleEmailLogin(values);
    } finally {
      setSubmitting(false);
    }
  };

  /** "Back to sign in" from the second factor: end the half-finished session. */
  const abandonFactor = async () => {
    setStage("signin");
    setError(null);
    try {
      await postLogout();
    } catch {
      /* already gone — the next sign-in regenerates it regardless */
    }
    resetCsrfToken();
  };

  const openReset = () => {
    setError(null);
    setResetSentTo(null);
    setStage("reset");
  };

  const requestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!isEmail(address)) {
      setError(t("emailValidationError", "Please Enter a Valid Email Address"));
      return;
    }
    setResetBusy(true);
    setError(null);
    try {
      await apiRequest("POST", "/api/auth/forgot-password", { email: address });
      // The server answers the same way for an unknown address, so this must
      // too — anything more specific is an account-existence oracle.
      setResetSentTo(address);
    } catch (err) {
      const reason = localizeApiMessage(describeApiError(err), t);
      setError(parseApiError(err).status === 429 ? reason : `${t("failedToSendReset", "Failed to send reset email. Please try again.")} ${reason}`);
    } finally {
      setResetBusy(false);
    }
  };

  const handleLinkAccount = async () => {
    if (!linkPassword || !pendingLinkInfo) return;

    setIsLinking(true);
    try {
      await apiRequest("POST", "/api/auth/link-account", { password: linkPassword });

      localStorage.removeItem('selectedProject');
      localStorage.removeItem('selectedPhase');
      localStorage.removeItem('isProjectSelected');

      toast({
        title: t("accountsLinked", "Accounts Linked"),
        description: t("accountLinkedSuccessfully", "Your {provider} account has been linked successfully.").replace('{provider}', pendingLinkInfo.provider),
      });

      setShowLinkDialog(false);
      setLinkPassword("");
      setPendingLinkInfo(null);

      await checkSession();
    } catch (error: any) {
      toast({
        title: t("linkingFailed", "Linking failed"),
        description: (error.message && localizeApiMessage(error.message, t)) || t("invalidPasswordTryAgain", "Invalid password. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsLinking(false);
    }
  };

  // OAuth providers arrive lowercase ("google"); present them Title-Cased in copy.
  const providerLabel = pendingLinkInfo?.provider
    ? pendingLinkInfo.provider.charAt(0).toUpperCase() + pendingLinkInfo.provider.slice(1)
    : '';

  const typedEmail = email;
  const factorHeading = {
    title: t("shellAuthConfirmItsYou", "Confirm It's You"),
    body: t("shellAuthPasswordRightOneMoreStep", "Your password was right. One more step for {email}.", {
      email: typedEmail || t("shellAuthYourAccount", "your account"),
    }),
  };

  /*
    PropPay's staff sign-in, ported (Andrew, 2026-09-27: "copy the one from
    PropPay … remove the images on the left. Use just the section on the
    right"). What was the right-hand column is now the whole page: the
    greeting over one elevated card, the ways in stacked inside it —
    passkey first (recommended), then the two identity providers, then email
    and password in an inset panel of its own — and the legal footer under it.

    The email form stays OPEN, not behind PropPay's "Sign in with email"
    button, and that is load-bearing here: the passkey conditional-UI ceremony
    starts on mount and is offered from this field's autofill
    (`autocomplete="username webauthn"`), so the input has to exist from the
    first paint.

    Above the card: why they are here, when a session ended under them
    (SessionEndedCard, G5 — a nudge card, rendered last). Inside it: one task at a time — the ways in, the
    second factor, or the reset link — with no route change between them (G10).
  */
  return (
    <AuthPageShell
      title={appName ? t("shellAuthSignInToApp", "Sign in to {app}", { app: appName }) : t("signIn", "Sign In")}
      subtitle={
        appName
          ? t("shellAuthSignInToAppTagline", "Use your SaleFish account. You'll go back to {app} when you're done.", { app: appName })
          : t("signInTagline", "Your projects, contacts and inventory in one place.")
      }
    >
          <AuthCard contentClassName="space-y-2.5">
              {/* The Unlock screen: the sign-in menu with everything but the
                  passkey taken off it, on the installed app only (G9). */}
              {stage === "signin" && resume && (
                <div className="space-y-2.5" data-testid="passkey-unlock">
                  <Button
                    size="lg"
                    onClick={handleUnlock}
                    disabled={unlocking}
                    className="w-full"
                    data-testid="button-passkey-unlock"
                  >
                    <KeyRound />
                    {unlocking ? t("shellAuthWaitingForDevice", "Waiting for Your Device…") : t("shellAuthUnlockSaleFish", "Unlock SaleFish")}
                  </Button>
                  <p className="px-2 pb-1 text-center text-caption text-muted-foreground">
                    {t(
                      "shellAuthUnlockExplainer",
                      "Sessions close automatically when you’re away. Unlock with the passkey on this device to pick up where you left off.",
                    )}
                  </p>
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setError(null);
                      setResume(false);
                    }}
                    disabled={unlocking}
                    data-testid="button-unlock-another-way"
                  >
                    {t("shellAuthUseAnotherWay", "Use Another Way to Sign In")}
                  </Button>
                </div>
              )}

              {stage === "signin" && !resume && (
                <>
              {/* Recommended */}
              {passkeySupported && (
                <Button
                  size="lg"
                  onClick={handlePasskeyLogin}
                  className="w-full"
                  data-testid="button-passkey-login"
                >
                  <KeyRound />
                  {t("shellAuthSignInWithPasskey", "Sign in with a Passkey")}
                </Button>
              )}

              {/* Federated identity */}
              <Button
                size="lg"
                variant="outline"
                onClick={() => handleOAuthLogin('google')}
                className="w-full"
                data-testid="button-google-login"
              >
                <GoogleLogo size={16} />
                {t("shellAuthContinueWithGoogle", "Continue with Google")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => handleOAuthLogin('microsoft')}
                className="w-full"
                data-testid="button-microsoft-login"
              >
                <MicrosoftLogo size={16} />
                {t("shellAuthContinueWithMicrosoft", "Continue with Microsoft")}
              </Button>

              {/* Email & password — collapsed to one more way in until asked
                  for, then its own inset panel, as PropPay has it. */}
              {!showEmail ? (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={revealEmail}
                  className="w-full"
                  aria-expanded={false}
                  data-testid="button-show-email-login"
                >
                  <Mail />
                  {t("shellAuthSignInWithEmail", "Sign in with Email")}
                </Button>
              ) : (
                <form
                  onSubmit={submitEmailLogin}
                  className="space-y-3.5 border-t border-border pt-4"
                  noValidate
                >
                  <div className="space-y-2">
                    <Label htmlFor={emailId} className={cn(fieldErrors.email && "text-destructive")}>{t("email", "Email")}</Label>
                    <Input
                      ref={emailRef}
                      id={emailId}
                      name="email"
                      value={email}
                      type="text"
                      inputMode="email"
                      enterKeyHint="next"
                      placeholder={t("enterEmail", "Enter Your Email")}
                      className="h-11 px-3.5 text-body"
                      data-testid="input-email"
                      autoComplete="username webauthn"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      aria-invalid={!!fieldErrors.email}
                      aria-describedby={fieldErrors.email ? `${emailId}-message` : undefined}
                      onChange={(e) => {
                        const next = e.target.value.toLowerCase();
                        setEmail(next);
                        recheck("email", { email: next, password });
                      }}
                    />
                    {fieldErrors.email && (
                      <p id={`${emailId}-message`} className="text-label font-medium text-destructive">{fieldErrors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={passwordId} className={cn(fieldErrors.password && "text-destructive")}>{t("password", "Password")}</Label>
                    <div className="relative">
                      <Input
                        ref={passwordRef}
                        id={passwordId}
                        name="password"
                        value={password}
                        type={showPassword ? "text" : "password"}
                        placeholder={t("enterPassword", "Enter Your Password")}
                        className="h-11 px-3.5 pr-11 text-body"
                        autoComplete="current-password"
                        data-testid="input-password"
                        aria-invalid={!!fieldErrors.password}
                        aria-describedby={fieldErrors.password ? `${passwordId}-message` : undefined}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          recheck("password", { email, password: e.target.value });
                        }}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 z-10 flex size-6 -translate-y-1/2 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? t("hidePassword", "Hide Password") : t("showPassword", "Show Password")}
                        data-testid="button-toggle-password"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
                      </button>
                    </div>
                    {fieldErrors.password && (
                      <p id={`${passwordId}-message`} className="text-label font-medium text-destructive">{fieldErrors.password}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="remember"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(!!checked)}
                      data-testid="checkbox-remember-me"
                    />
                    <label htmlFor="remember" className="cursor-pointer text-label text-muted-foreground">
                      {t("rememberMe", "Remember Me")}
                    </label>
                  </div>

                  <Button
                    size="lg"
                    type="submit"
                    className="mt-1 w-full"
                    disabled={submitting || finishing}
                    data-testid="button-login"
                  >
                    {submitting || finishing ? t("loggingIn", "Logging in...") : t("login", "Sign In")}
                  </Button>

                  {/* In the card, not a route: the address is usually already
                      typed by the time somebody realises they can't remember
                      the password, and /forgot-password would throw it away.
                      That page still works for a direct link. */}
                  <button
                    type="button"
                    onClick={openReset}
                    className="hit-target block w-full text-center text-label font-semibold text-muted-foreground hover:text-foreground hover:underline"
                    data-testid="link-forgot-password"
                  >
                    {t("forgotPassword", "Forgot password?")}
                  </button>
                </form>
              )}
                </>
              )}

              {/* The second factor — the panel between a correct password and
                  a session. */}
              {stage === "factor" && (
                <Suspense fallback={<SecondFactorSkeleton heading={factorHeading} />}>
                <SecondFactorPanel
                  heading={factorHeading}
                  onVerified={() => void finishSignIn()}
                  onCancel={() => void abandonFactor()}
                  cancelLabel={t("backToLogin", "Back to Sign In")}
                  disabled={finishing}
                />
                </Suspense>
              )}

              {/* Reset — asks for the link without leaving the card. The link in
                  the email opens /reset-password, as it always has. */}
              {stage === "reset" && (
                <form
                  onSubmit={requestReset}
                  className="space-y-3.5"
                  noValidate
                  data-testid="panel-reset"
                >
                  <div>
                    <h2 className="text-body font-semibold">{t("resetYourPassword", "Reset Your Password")}</h2>
                    <p className="mt-0.5 text-label text-muted-foreground">{t("enterEmailForReset", "Enter your email to receive a password reset link")}</p>
                  </div>

                  {resetSentTo ? (
                    <AuthMessage tone="success" testId="text-reset-sent">
                      {t("resetEmailSentDescription", "If an account exists with this email, you'll receive password reset instructions shortly.")} ({resetSentTo})
                    </AuthMessage>
                  ) : (
                    <>
                      <div className="space-y-1.5">
                        <Label htmlFor="reset-email">{t("emailAddress", "Email Address")}</Label>
                        <Input
                          id="reset-email"
                          type="email"
                          value={typedEmail}
                          onChange={(e) => setEmail(e.target.value.toLowerCase())}
                          placeholder={t("enterEmail", "Enter Your Email")}
                          className="h-11 px-3.5 text-body"
                          autoComplete="username"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                          data-testid="input-reset-email"
                        />
                      </div>
                      <Button
                        size="lg"
                        type="submit"
                        className="w-full"
                        disabled={resetBusy || !typedEmail}
                        data-testid="button-send-reset"
                      >
                        <Mail />
                        {resetBusy ? t("sendingResetLink", "Sending...") : t("sendResetLink", "Send Reset Link")}
                      </Button>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      setStage("signin");
                      setError(null);
                    }}
                    className="hit-target flex w-full items-center justify-center gap-1.5 text-label font-semibold text-muted-foreground hover:text-foreground hover:underline"
                    data-testid="button-reset-back"
                  >
                    <ArrowLeft className="size-[15px]" aria-hidden="true" />
                    {t("backToLogin", "Back to Sign In")}
                  </button>
                </form>
              )}

              {error && <AuthMessage testId="text-login-error">{error}</AuthMessage>}

              {/* Signing in for another app: a way back to it without signing
                  in (a real page load; the app hears "cancelled"). */}
              {appSignIn && appName && stage === "signin" && (
                <button
                  type="button"
                  onClick={() => {
                    clearAppSignIn();
                    window.location.assign(appSignInAbortPath(appSignIn));
                  }}
                  className="hit-target flex w-full items-center justify-center gap-1.5 text-label font-semibold text-muted-foreground hover:text-foreground hover:underline"
                  data-testid="button-app-signin-cancel"
                >
                  <ArrowLeft className="size-[15px]" aria-hidden="true" />
                  {t("shellAuthBackToApp", "Cancel and Go Back to {app}", { app: appName })}
                </button>
              )}
          </AuthCard>

      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-link-account">
          <DialogHeader>
            <DialogTitle>
              {t("linkYourAccounts", "Connect Your {provider} Sign-In").replace('{provider}', providerLabel)}
            </DialogTitle>
            <DialogDescription>
              {t("accountExistsEnterPassword", "You already have a SaleFish account for {email}. Enter that account's password once to connect {provider}. After this, you can sign in with your password or with {provider}.")
                .replace('{email}', pendingLinkInfo?.email || '')
                .replaceAll('{provider}', providerLabel)}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <form
              onSubmit={(e) => { e.preventDefault(); handleLinkAccount(); }}
              className="flex flex-col gap-2"
            >
              <label htmlFor="link-password" className="text-label font-medium text-foreground">
                {t("linkAccountsPasswordLabel", "Your SaleFish Password")}
              </label>
              <div className="relative">
                <Input
                  id="link-password"
                  type={showLinkPassword ? "text" : "password"}
                  value={linkPassword}
                  onChange={(e) => setLinkPassword(e.target.value)}
                  className="h-12 px-4 pr-12"
                  placeholder={t("enterPassword", "Enter Your Password")}
                  autoComplete="current-password"
                  autoFocus
                  data-testid="input-link-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowLinkPassword(!showLinkPassword)}
                  aria-label={showLinkPassword ? t("hidePassword", "Hide Password") : t("showPassword", "Show Password")}
                  data-testid="button-toggle-link-password"
                  tabIndex={-1}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    {showLinkPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </div>
                </button>
              </div>
              <p className="text-caption text-muted-foreground pt-1">
                {t("linkAccountsHint", "This is the password you already use to sign in with email — not your {provider} password.").replace('{provider}', providerLabel)}
              </p>
              <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
            </form>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)} data-testid="button-cancel-link">
              {t("cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleLinkAccount}
              disabled={isLinking || !linkPassword}
              data-testid="button-confirm-link"
            >
              {isLinking ? t("linking", "Connecting…") : t("linkAccounts", "Connect {provider}").replace('{provider}', providerLabel)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Why they are here, when a session ended under them (G5). A nudge
          card: bottom drawer on a phone, floating card on a desk. */}
      <SessionEndedCard />
    </AuthPageShell>
  );
}
